// Which stage screen the pill opens for a call's patient, and what its button says.
//
// Neither decision is made here. `resolveLeadActionPopup` (utils/leadActionPopupResolver)
// already owns "which logical popup does this status open?", and the Active Leads tables
// already own the wording (pages/dashboard/tables/AllLeadTable/CotactCell.jsx). This file
// only joins the two, so a call's patient opens the same screen, under the same name, as
// that patient's own stage table:
//
//   New        -> Contact follow-up            (POPUP.FOLLOW_UP)
//   Booked     -> Record Attended              (POPUP.CONSULTATION_BOOKED)
//   Attended   -> Follow up                    (POPUP.ATTENDED)
//   FTA        -> Follow up ("What's next?":   (POPUP.FTA)
//                 Reschedule / Cancel booking)
//
// Treatment started, closed and archived have no pop-up of their own, so they open the
// lead page — the same place their table's button goes.
import { POPUP, resolveLeadActionPopup } from '../../utils/leadActionPopupResolver';

// Verbatim from CotactCell.jsx, so one button means one thing wherever staff meet it.
// ("Contact lead" there is the never-contacted variant of Follow up; the pill has no
// contacted count, so it says Follow up.)
const LABEL = Object.freeze({
    [POPUP.FOLLOW_UP]: 'Follow up',
    [POPUP.CONSULTATION_BOOKED]: 'Record Attended',
    [POPUP.ATTENDED]: 'Follow up',
    [POPUP.FTA]: 'Follow up',
    [POPUP.TREATMENT_STARTED]: 'Treatment in progress',
    [POPUP.READ_ONLY]: 'Open patient',
});

// The stages with a screen that can open over the pill. Everything else opens the lead page.
export const POPUP_STAGES = Object.freeze([
    POPUP.FOLLOW_UP, POPUP.CONSULTATION_BOOKED, POPUP.ATTENDED, POPUP.FTA,
]);

// The stage of a call's patient, or null when this caller is not a patient yet, or the
// payload carries no summary (an older backend, before live_calls sent lead_summary).
// Without a stage the pill keeps its plain "Open patient", which navigates as before.
export const stageOf = ({ leadId, status, source = 'call-pill' }) => {
    if (!leadId || !status) return null;
    const { popup } = resolveLeadActionPopup({ status, leadId, source });
    return {
        popup,
        leadId,
        status,
        label: LABEL[popup] || 'Open patient',
        // false -> the lead page, as the stage's own table button does.
        inPopup: POPUP_STAGES.includes(popup),
    };
};

// A live call carries its patient's stage in `lead_summary` (apps/voice/live.py).
// The Call Logs row carries the same thing on `lead.status` (call_logs.row_data)
// and calls `stageOf` directly, so both screens open the same pop-up under the
// same name.
export const stageFor = (call) =>
    stageOf({ leadId: call?.lead?.id, status: call?.leadSummary?.status, source: 'call-pill' });
